import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * v2 tooltip — navy chrome surface (informational, not an action).
 *
 * Positioned in a body portal rather than absolutely inside the anchor, so a
 * tooltip on text near the right edge of a narrow pane can never widen its
 * container: the bubble is clamped into the viewport instead. Reveal is
 * immediate — the only delay is the 100ms opacity transition.
 */
export type TooltipSide = 'top' | 'bottom';

const GAP = 6;
const EDGE = 8;

const BUBBLE_CLASS =
  'pointer-events-none fixed left-0 top-0 z-50 max-w-[min(320px,calc(100vw-16px))] ' +
  'whitespace-normal break-words rounded-control bg-chrome px-s3 py-s2 text-left text-meta ' +
  'font-normal leading-[1.45] text-white shadow-floating transition-opacity duration-100';

/**
 * The anchor-side wiring: hover/focus handlers, the describedby link, and the
 * positioned bubble to render. Components that own their own anchor element
 * (truncating text, for one) use this directly; `Tooltip` wraps it in a span.
 */
export const useTooltip = (content: React.ReactNode, side: TooltipSide = 'top', enabled = true) => {
  const id = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const active = enabled && open && content != null && content !== '';

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;
    const a = anchor.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();
    const fitsAbove = a.top - b.height - GAP >= EDGE;
    const fitsBelow = a.bottom + b.height + GAP <= window.innerHeight - EDGE;
    const above = side === 'top' ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
    const top = above ? a.top - b.height - GAP : a.bottom + GAP;
    // Clamp horizontally so the bubble never pushes past either viewport edge.
    const maxLeft = Math.max(EDGE, window.innerWidth - b.width - EDGE);
    const left = Math.min(Math.max(a.left + a.width / 2 - b.width / 2, EDGE), maxLeft);
    // The app renders under a CSS `zoom`, so client rects are visual pixels
    // while a transform is applied in the element's own scaled pixels. Divide
    // by the measured ratio or every tooltip lands 10% too far right.
    const scale = bubble.offsetWidth > 0 ? b.width / bubble.offsetWidth : 1;
    setPos({ top: top / scale, left: left / scale });
  }, [side]);

  useLayoutEffect(() => {
    if (!active) {
      setPos(null);
      return;
    }
    place();
  }, [active, content, place]);

  useEffect(() => {
    if (!active) return undefined;
    const onScroll = () => place();
    // The bubble's own box settles a frame late (wrapping, web-font swap), and
    // a stale width is what lets a clamped tooltip drift back off-screen.
    const observer =
      typeof ResizeObserver === 'undefined' || !bubbleRef.current ? null : new ResizeObserver(() => place());
    observer?.observe(bubbleRef.current as Element);
    const raf = requestAnimationFrame(() => place());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('keydown', onKey);
    };
  }, [active, place]);

  const anchorProps = {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
    },
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
    'aria-describedby': active ? id : undefined,
  };

  const bubble =
    active && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={bubbleRef}
            id={id}
            role="tooltip"
            style={{ transform: `translate3d(${pos?.left ?? 0}px, ${pos?.top ?? 0}px, 0)` }}
            className={`${BUBBLE_CLASS} ${pos ? 'opacity-100' : 'opacity-0'}`}
          >
            {content}
          </div>,
          document.body,
        )
      : null;

  return { anchorProps, bubble, open: active };
};

export interface TooltipProps {
  content: React.ReactNode;
  side?: TooltipSide;
  className?: string;
  /** Suppresses the tooltip without changing the anchor's layout. */
  disabled?: boolean;
  children: React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({ content, side = 'top', className = '', disabled, children }) => {
  const { anchorProps, bubble } = useTooltip(content, side, !disabled);
  return (
    <span {...anchorProps} className={`inline-flex ${className}`}>
      {children}
      {bubble}
    </span>
  );
};

export default Tooltip;
